/**
 * Conversations WhatsApp et SMS (docs/CONVERSATIONS.md) : entrées et sorties des routes et des outils MCP (fil d'un
 * objet, préparation et envoi d'un message, administration des numéros et des modèles).
 */
import { z } from 'zod';
import {
  CANAUX_CONVERSATION,
  CATEGORIES_META,
  FINALITES_CONSENTEMENT,
  schemaVariablesModele,
  USAGES_NUMERO,
} from './conversations';
import { schemaCodeFiliale } from './schemas';

const objet = { ressource: z.string().min(1).max(100), objetId: z.string().min(1).max(100) };

/** Fil d'échanges d'un objet AGORA (ticket, incident, affaire, tiers). */
export const schemaFilConversation = z.object(objet).strict();

/**
 * Destinataire : un contact AGORA (son mobile, sinon son téléphone), un numéro saisi, ou le demandeur de l'objet (incident
 * IT : le numéro de son compte, recopié d'Entra).
 */
const schemaDestinataire = z.union([
  z.object({ contactId: z.guid() }).strict(),
  z.object({ numero: z.string().trim().min(3).max(40) }).strict(),
  z.object({ demandeur: z.literal(true) }).strict(),
]);

/** Message à préparer (sans rien écrire) ou à envoyer : un modèle approuvé ou un texte libre, jamais les deux. */
export const schemaMessageConversation = z.object({
  ...objet,
  destinataire: schemaDestinataire,
  canal: z.enum(CANAUX_CONVERSATION).optional(),
  modele: z.object({ code: z.string().min(1).max(100), variables: schemaVariablesModele.optional() }).strict().optional(),
  texte: z.string().trim().min(1).max(4096).optional(),
  /** Traduction relue par l'auteur (W6) : c'est elle qui part. */
  traduction: z.object({ langue: z.string().regex(/^[a-z]{2,3}$/), texte: z.string().trim().min(1).max(4096) }).strict().optional(),
}).strict().refine((m) => !!m.modele !== !!m.texte, { message: 'Un modèle ou un texte, jamais les deux', path: ['texte'] });

/** Modèles utilisables pour un objet (ceux de son nœud, actifs). */
export const schemaModelesObjet = schemaFilConversation;

/** Boîte « À trier » d'une filiale (conversations sans objet, ouvertes aux groupes de tri du numéro). */
export const schemaATrier = z.object({ filiale: schemaCodeFiliale }).strict();

/** Rattachement d'une conversation à un objet (incident, affaire, tiers) : ses messages sans objet le suivent. */
export const schemaRattachementConversation = z.object(objet).strict();

export const schemaNumeroCanal = z.object({
  canal: z.enum(CANAUX_CONVERSATION),
  numero: z.string().regex(/^\+[1-9][0-9]{6,14}$/).nullable(),
  expediteur: z.string().regex(/^[A-Za-z0-9]{1,11}$/).nullable(),
  identifiantAws: z.string().min(1).max(300),
  nomAffiche: z.string().trim().min(1).max(100),
  usage: z.enum(USAGES_NUMERO),
  filiales: z.array(schemaCodeFiliale).min(1),
  groupesTri: z.record(schemaCodeFiliale, z.array(z.string().regex(/^AGORA-[A-Z0-9_-]+$/)).max(20)).default({}),
  actif: z.boolean().default(true),
}).strict()
  .refine((n) => n.numero !== null || n.expediteur !== null, { message: 'Numéro ou expéditeur obligatoire', path: ['numero'] })
  .refine((n) => n.canal === 'sms' || n.numero !== null, { message: 'Numéro obligatoire pour WhatsApp', path: ['numero'] });

export const schemaModeleConversation = z.object({
  code: z.string().regex(/^[a-z0-9]+([.-][a-z0-9]+)*$/).max(100),
  canal: z.enum(CANAUX_CONVERSATION),
  finalite: z.enum(FINALITES_CONSENTEMENT),
  categorieMeta: z.enum(CATEGORIES_META).nullable(),
  noeud: z.string().regex(/^[a-z0-9-]+(\.[a-z0-9-]+)*$/),
  textes: z.record(z.string().regex(/^[a-z]{2,3}$/), z.string().min(1).max(1024)).refine((t) => Object.keys(t).length > 0, 'Au moins une langue'),
  nomMeta: z.string().regex(/^[a-z0-9_]{1,512}$/).nullable(),
  actif: z.boolean().default(true),
}).strict()
  .refine((m) => (m.canal === 'whatsapp') === (m.categorieMeta !== null), { message: 'Catégorie Meta pour WhatsApp seulement', path: ['categorieMeta'] })
  .refine((m) => m.canal === 'whatsapp' || m.finalite === 'service', { message: 'Pas de SMS marketing (W4)', path: ['finalite'] })
  .refine((m) => m.canal === 'sms' || m.nomMeta !== null, { message: 'Nom du modèle chez Meta obligatoire pour WhatsApp', path: ['nomMeta'] });

export type MessageConversation = z.infer<typeof schemaMessageConversation>;
export type NumeroCanal = z.infer<typeof schemaNumeroCanal>;
export type ModeleConversation = z.infer<typeof schemaModeleConversation>;
